/**
 * Sprint 3.3 — Video Module Frontend
 *
 * Generate AI videos via /video/generate (async job polling).
 * Library shows completed videos with hover-to-play cards.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Film,
  Play,
  Download,
  Clock,
  Zap,
  Lock,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import {
  generateVideo,
  getVideoJob,
  getVideoJobs,
  getVideoLibrary,
  cancelVideoJob,
  getVideoQuota,
  VideoJob,
  VideoQuota,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ProductionList from '../VideoProduction/ProductionList';
import './Video.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Constants ─────────────────────────────────────────────────────────────────

const DURATION_OPTIONS = [
  { value: 3, label: '3s' },
  { value: 5, label: '5s' },
  { value: 10, label: '10s (Enterprise)' },
];

const ASPECT_RATIO_OPTIONS = [
  { value: '16:9', label: '16:9 Landscape' },
  { value: '9:16', label: '9:16 Portrait' },
  { value: '1:1', label: '1:1 Square' },
  { value: '4:3', label: '4:3 Standard' },
];

const POLL_INTERVAL_MS = 5000;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function elapsedSeconds(createdAt: string): number {
  return Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
}

function formatElapsed(secs: number): string {
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function getUserTier(user: any): string {
  return (user?.tier || user?.plan || 'trial').toLowerCase();
}

// ── Component ─────────────────────────────────────────────────────────────────

const VideoModule: React.FC = () => {
  const { user } = useAuth();
  const tier = getUserTier(user);
  const isAdminUser = user?.role === 'admin' || user?.role === 'superadmin';
  const isProOrAbove = isAdminUser || tier === 'pro' || tier === 'enterprise';
  const isEnterprise = isAdminUser || tier === 'enterprise';

  // ── Tab
  const [activeTab, setActiveTab] = useState<'generate' | 'library' | 'productions'>('generate');

  // ── Form
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState('16:9');

  // ── Generation state
  const [generating, setGenerating] = useState(false);
  const [activeJob, setActiveJob] = useState<VideoJob | null>(null);
  const [recentJobs, setRecentJobs] = useState<VideoJob[]>([]);
  const [library, setLibrary] = useState<VideoJob[]>([]);
  const [quota, setQuota] = useState<VideoQuota | null>(null);
  const [error, setError] = useState('');

  // ── Elapsed timer
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef<NodeJS.Timeout | null>(null);

  // ── Polling
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // ── Library load-more
  const [libraryOffset, setLibraryOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);

  // ── Load on mount ─────────────────────────────────────────────────────────

  const loadInitialData = useCallback(async () => {
    try {
      const [q, jobs, lib] = await Promise.all([
        getVideoQuota().catch(() => null),
        getVideoJobs(5, 0).catch(() => [] as VideoJob[]),
        getVideoLibrary(20, 0).catch(() => [] as VideoJob[]),
      ]);
      if (q) setQuota(q);
      setRecentJobs(jobs);
      setLibrary(lib);
      setHasMore(lib.length === 20);
      // If there's a pending/processing job in recent, resume polling it
      const inProgress = jobs.find(
        j => j.status === 'pending' || j.status === 'processing',
      );
      if (inProgress) {
        setActiveJob(inProgress);
      }
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // ── Polling ───────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (job: VideoJob) => {
      stopPolling();

      // Start elapsed counter
      const startedAt = job.created_at;
      setElapsed(elapsedSeconds(startedAt));
      elapsedRef.current = setInterval(() => {
        setElapsed(elapsedSeconds(startedAt));
      }, 1000);

      // Poll job status
      pollingRef.current = setInterval(async () => {
        try {
          const updated = await getVideoJob(job.id);
          setActiveJob(updated);
          if (updated.status === 'complete' || updated.status === 'failed') {
            stopPolling();
            // Refresh quota and lists
            getVideoQuota()
              .then(q => setQuota(q))
              .catch(() => {});
            getVideoLibrary(20, 0)
              .then(lib => {
                setLibrary(lib);
                setHasMore(lib.length === 20);
              })
              .catch(() => {});
          }
        } catch {
          // ignore transient poll errors
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling],
  );

  // Start polling whenever activeJob becomes pending/processing
  useEffect(() => {
    if (
      activeJob &&
      (activeJob.status === 'pending' || activeJob.status === 'processing')
    ) {
      startPolling(activeJob);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.id, activeJob?.status]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // ── Generate ──────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError('');
    setActiveJob(null);
    try {
      const res = await generateVideo({
        prompt: prompt.trim(),
        duration,
        aspect_ratio: aspectRatio,
      });
      // Fetch full job details immediately
      const job = await getVideoJob(res.job_id);
      setActiveJob(job);
      setRecentJobs(prev => [job, ...prev].slice(0, 5));
    } catch (e: any) {
      const detail = getApiError(e, 'Video generation failed');
      if (e?.response?.status === 429) {
        setError('Monthly video generation limit reached. Upgrade your plan for more.');
      } else {
        setError(detail);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCancel = async () => {
    if (!activeJob) return;
    try {
      await cancelVideoJob(activeJob.id);
      stopPolling();
      setActiveJob(prev => prev ? { ...prev, status: 'failed', error: 'Cancelled' } : null);
    } catch {
      // ignore
    }
  };

  // ── Load more library ─────────────────────────────────────────────────────

  const handleLoadMore = async () => {
    setLibraryLoading(true);
    try {
      const nextOffset = libraryOffset + 20;
      const more = await getVideoLibrary(20, nextOffset);
      setLibrary(prev => [...prev, ...more]);
      setLibraryOffset(nextOffset);
      setHasMore(more.length === 20);
    } catch {
      // ignore
    } finally {
      setLibraryLoading(false);
    }
  };

  // ── Quota helpers ─────────────────────────────────────────────────────────

  const quotaPercent =
    quota && !quota.unlimited && quota.monthly_limit
      ? (quota.used_this_month / quota.monthly_limit) * 100
      : 0;
  const quotaNearLimit = quotaPercent >= 80;
  const quotaExceeded =
    quota && !quota.unlimited && quota.monthly_limit != null &&
    quota.used_this_month >= quota.monthly_limit;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="video-module">
      {/* Module header */}
      <div className="video-module-header">
        <h1 className="video-module-title">
          <Film size={24} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
          Video
        </h1>
      </div>

      {/* Tabs */}
      <div className="video-tabs">
        <button
          className={`video-tab${activeTab === 'generate' ? ' video-tab--active' : ''}`}
          onClick={() => setActiveTab('generate')}
        >
          <Zap size={15} style={{ marginRight: 6 }} />
          Generate
        </button>
        <button
          className={`video-tab${activeTab === 'library' ? ' video-tab--active' : ''}`}
          onClick={() => setActiveTab('library')}
        >
          <Film size={15} style={{ marginRight: 6 }} />
          Library
        </button>
        <button
          className={`video-tab${activeTab === 'productions' ? ' video-tab--active' : ''}`}
          onClick={() => setActiveTab('productions')}
        >
          <Film size={15} style={{ marginRight: 6 }} />
          Productions
        </button>
      </div>

      {/* ── Generate Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'generate' && (
        <div className="video-generate-tab">
          {!isProOrAbove ? (
            /* Tier gate */
            <GlassCard padding="lg">
              <div className="video-locked-panel">
                <Lock size={36} className="video-locked-icon" />
                <h3 className="video-locked-title">Pro Plan Required</h3>
                <p className="video-locked-body">
                  Video generation is available on Pro and above. Upgrade your plan to
                  create AI-generated videos from text prompts.
                </p>
                <GlassBadge variant="warning">
                  Current plan: {tier}
                </GlassBadge>
              </div>
            </GlassCard>
          ) : (
            <>
              {/* Generation form */}
              <GlassCard padding="lg">
                <div className="video-generate-form">
                  <div className="video-form-header">
                    <div>
                      <h2 className="video-form-title">Generate Video</h2>
                      <p className="video-form-subtitle">
                        Create AI-generated videos from a text prompt.
                      </p>
                    </div>
                    <GlassBadge variant="info">Powered by RunwayML</GlassBadge>
                  </div>

                  {/* Prompt */}
                  <div className="video-field">
                    <label className="video-label">Prompt</label>
                    <textarea
                      className="video-textarea"
                      placeholder="Describe the video you want to create. Be specific about subjects, actions, lighting, and style..."
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                      rows={4}
                    />
                  </div>

                  {/* Controls row */}
                  <div className="video-controls-row">
                    {/* Duration */}
                    <div className="video-field">
                      <label className="video-label">Duration</label>
                      <select
                        className="video-select"
                        value={duration}
                        onChange={e => setDuration(Number(e.target.value))}
                      >
                        {DURATION_OPTIONS.map(opt => (
                          <option
                            key={opt.value}
                            value={opt.value}
                            disabled={opt.value === 10 && !isEnterprise}
                            title={opt.value === 10 && !isEnterprise ? 'Enterprise only' : undefined}
                          >
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Aspect ratio */}
                    <div className="video-field">
                      <label className="video-label">Aspect Ratio</label>
                      <select
                        className="video-select"
                        value={aspectRatio}
                        onChange={e => setAspectRatio(e.target.value)}
                      >
                        {ASPECT_RATIO_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Quota */}
                  {quota && (
                    <>
                      <div className={`video-quota-badge${quotaNearLimit ? ' warning' : ''}`}>
                        <Zap size={13} />
                        {quota.unlimited
                          ? 'Unlimited videos'
                          : `${quota.used_this_month} / ${quota.monthly_limit} videos this month`}
                        <GlassBadge variant={quotaNearLimit ? 'warning' : 'default'}>
                          {quota.tier}
                        </GlassBadge>
                      </div>
                      {quota.productions_monthly_limit !== undefined &&
                        quota.productions_monthly_limit !== null && (
                          <div className="video-quota-badge">
                            <Film size={13} />
                            {quota.unlimited
                              ? 'Unlimited productions'
                              : `${quota.productions_used_this_month ?? 0} / ${quota.productions_monthly_limit} productions this month`}
                            {quota.max_scenes_per_production !== undefined &&
                              quota.max_scenes_per_production > 0 && (
                                <GlassBadge variant="default">
                                  up to {quota.max_scenes_per_production} scenes
                                </GlassBadge>
                              )}
                          </div>
                        )}
                    </>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="video-error">
                      <AlertCircle size={14} />
                      {error}
                    </div>
                  )}

                  {/* Generate button */}
                  <GlassButton
                    variant="primary"
                    icon={<Film size={16} />}
                    onClick={handleGenerate}
                    disabled={generating || !prompt.trim() || !!quotaExceeded}
                    style={{ width: '100%' }}
                  >
                    {generating ? 'Submitting...' : 'Generate Video'}
                  </GlassButton>
                </div>
              </GlassCard>

              {/* Active job progress */}
              {activeJob && (activeJob.status === 'pending' || activeJob.status === 'processing') && (
                <GlassCard padding="lg">
                  <div className="video-progress-section">
                    <div className="video-progress-top">
                      <div className="video-progress-indicator">
                        <div className="video-progress-pulse" />
                        <span className="video-progress-label">Generating your video...</span>
                      </div>
                      <GlassBadge variant="info">Using RunwayML Gen-3</GlassBadge>
                    </div>

                    <div className="video-progress-meta">
                      <span className="video-progress-elapsed">
                        <Clock size={13} style={{ marginRight: 4 }} />
                        {formatElapsed(elapsed)} elapsed
                      </span>
                      <GlassBadge variant="default">
                        {activeJob.status === 'pending' ? 'Queued' : 'Rendering'}
                      </GlassBadge>
                    </div>

                    <div className="video-progress-prompt">{activeJob.prompt}</div>

                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={handleCancel}
                    >
                      Cancel
                    </GlassButton>
                  </div>
                </GlassCard>
              )}

              {/* Completed result */}
              {activeJob && activeJob.status === 'complete' && activeJob.result_url && (
                <GlassCard padding="lg">
                  <div className="video-result">
                    <video
                      className="video-result-player"
                      src={activeJob.result_url}
                      controls
                      autoPlay
                      loop
                    >
                      Your browser does not support the video element.
                    </video>

                    <div className="video-result-meta">
                      {activeJob.aspect_ratio && (
                        <GlassBadge variant="info">{activeJob.aspect_ratio}</GlassBadge>
                      )}
                      {activeJob.duration_seconds && (
                        <GlassBadge variant="default">
                          {activeJob.duration_seconds}s
                        </GlassBadge>
                      )}
                      <GlassBadge variant="default">{activeJob.provider}</GlassBadge>
                      <a
                        href={activeJob.result_url}
                        download={`video-${activeJob.id}.mp4`}
                        target="_blank"
                        rel="noreferrer"
                        className="video-download-link"
                      >
                        <GlassButton variant="ghost" size="sm" icon={<Download size={14} />}>
                          Download
                        </GlassButton>
                      </a>
                    </div>
                  </div>
                </GlassCard>
              )}

              {/* Failed result */}
              {activeJob && activeJob.status === 'failed' && (
                <GlassCard padding="lg">
                  <div className="video-error">
                    <AlertCircle size={14} />
                    {activeJob.error || 'Video generation failed.'}
                  </div>
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={<RefreshCw size={14} />}
                    onClick={handleGenerate}
                    style={{ marginTop: 'var(--space-3)' }}
                  >
                    Retry
                  </GlassButton>
                </GlassCard>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Productions Tab ──────────────────────────────────────────────── */}
      {activeTab === 'productions' && (
        <div className="video-productions-tab">
          <ProductionList />
        </div>
      )}

      {/* ── Library Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'library' && (
        <div className="video-library-tab">
          {library.length === 0 ? (
            <div className="video-empty-state">
              <Film size={48} strokeWidth={1} className="video-empty-icon" />
              <p className="video-empty-text">No videos generated yet.</p>
              <p className="video-empty-sub">
                Switch to Generate to create your first video.
              </p>
              <GlassButton
                variant="secondary"
                onClick={() => setActiveTab('generate')}
              >
                Go to Generate
              </GlassButton>
            </div>
          ) : (
            <>
              <div className="video-library-grid">
                {library.map(job => (
                  <VideoCard key={job.id} job={job} />
                ))}
              </div>

              {hasMore && (
                <div className="video-load-more">
                  <GlassButton
                    variant="secondary"
                    onClick={handleLoadMore}
                    disabled={libraryLoading}
                    icon={libraryLoading ? <RefreshCw size={14} /> : undefined}
                  >
                    {libraryLoading ? 'Loading...' : 'Load More'}
                  </GlassButton>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── VideoCard ─────────────────────────────────────────────────────────────────

interface VideoCardProps {
  job: VideoJob;
}

const VideoCard: React.FC<VideoCardProps> = ({ job }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <GlassCard padding="none" className="video-card">
      <div
        className="video-card-thumb"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {job.result_url ? (
          <>
            <video
              ref={videoRef}
              src={job.result_url}
              muted
              loop
              playsInline
              className="video-card-video"
            />
            <div className="video-card-overlay">
              <Play size={24} className="video-card-play-icon" />
            </div>
          </>
        ) : (
          <div className="video-card-placeholder">
            <Film size={32} strokeWidth={1} />
          </div>
        )}
      </div>

      <div className="video-card-body">
        <p className="video-card-prompt" title={job.prompt}>
          {job.prompt}
        </p>

        <div className="video-card-meta">
          {job.aspect_ratio && (
            <GlassBadge variant="info">{job.aspect_ratio}</GlassBadge>
          )}
          {job.duration_seconds && (
            <GlassBadge variant="default">{job.duration_seconds}s</GlassBadge>
          )}
          <span className="video-card-date">{formatDate(job.created_at)}</span>
        </div>

        {job.result_url && (
          <a
            href={job.result_url}
            download={`video-${job.id}.mp4`}
            target="_blank"
            rel="noreferrer"
            className="video-card-download"
            title="Download"
          >
            <Download size={14} />
          </a>
        )}
      </div>
    </GlassCard>
  );
};

export default VideoModule;
